import { Component, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { map, mergeMap, tap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ProductReferencesService } from '@services/product-references/product-references.service';
import { RefDataService } from '@services/ref-data.service';
import { JSONObject } from '@shared/models/json.model';

export class ProductNode {
  children: ProductNode[] | undefined;
  key: string | undefined;
  name: JSONObject | undefined;
  market: string | undefined;
  parent: ProductNode | undefined;
}

export class ProductReferencesObj {
  any:
    | {
        // any value
        key: string;
        order: string;
        nextOrder: string;
        name: JSONObject | undefined;
        externalId: string;
        market: string;
      }
    | undefined;
}

export class ProductHierarchyObj {
  children: ProductNode[] | undefined;
  key: string | undefined;
  name: JSONObject | undefined;
  level: number | undefined; // used just for find the root
}

export class InputCatalogObj {
  formRecipe: UntypedFormGroup | undefined;
  lang: string | undefined;
  market: string | undefined;
  domain: string | undefined;
  typeAbaque: boolean | undefined;
}

@Component({
  selector: 'app-catalog',
  templateUrl: 'catalog.component.html',
  styleUrls: ['./catalog.component.scss'],
  imports: [CommonModule, MatTreeModule, MatCheckboxModule, TranslateModule, MatButtonModule, MatIconModule],
})
export class CatalogComponent {
  formRecipe: UntypedFormGroup | undefined = this.formBuilder.group({});
  math = Math;
  typeAbaque: boolean | undefined = false;
  lang: string | undefined = '';
  market: string | undefined = '';
  domain: string | undefined = '';
  treeControl: NestedTreeControl<ProductNode>;
  productReferences = [];
  catalogs: MatTreeNestedDataSource<ProductNode>[] = [];

  constructor(
    public formBuilder: UntypedFormBuilder,
    private refDataService: RefDataService,
    private productReferencesService: ProductReferencesService,
  ) {
    this.treeControl = new NestedTreeControl<ProductNode>((node) => node.children);
  }

  @Input() set inputCatalogObj(catalogObj: InputCatalogObj) {
    this.formRecipe = catalogObj.formRecipe;
    this.lang = catalogObj.lang;
    this.market = catalogObj.market;
    this.domain = catalogObj.domain;
    this.typeAbaque = catalogObj.typeAbaque;
    this.initCatalog();
  }

  hasChild = (_: number, nodeData: ProductNode): boolean => !!nodeData.children && nodeData.children.length > 0;

  initCatalog(): void {
    if (this.formRecipe) {
      this.refDataService
        .getByKey('productReferences', true, this.domain, this.market, this.lang)
        .pipe(
          tap((productReferences) => (this.productReferences = productReferences ?? {})),
          map((productReferences) => Object.keys(productReferences)),
          map((keys) => keys.map((key) => this.productReferencesService.getHierarchy(key, this.market, true))),
          mergeMap((requests) => forkJoin(requests)),
        )
        .subscribe((productsHierarchyObjList: ProductHierarchyObj[]) => this.createTreeAndOpenIt(productsHierarchyObjList));
    }
  }

  createTreeAndOpenIt(productsHierarchyObjList: ProductHierarchyObj[]): void {
    productsHierarchyObjList.map((productsList) => {
      if (productsList) {
        productsList.level = 1;
        const dataSource = new MatTreeNestedDataSource<ProductNode>();
        // @ts-ignore
        dataSource.data = [productsList];
        this.catalogs.push(dataSource);
        this.formRecipe?.value.productReferences?.forEach((productKey: any) => {
          const list = this.getParentHierarchy(dataSource.data, productKey.key);
          list?.pop(); // remove current last node
          list?.forEach((parent) => {
            this.treeControl.expand(parent);
          });
        });
      }
    });
  }

  toggleProduct(node: ProductNode, products: ProductNode[]): void {
    const index = this.formRecipe?.value.productReferences.findIndex((keyProd: any) => keyProd.key === node.key);

    const resultValue: { key: string }[] = this.formRecipe?.value.productReferences;

    if (index === -1) {
      // add
      node.market = this.market;
      if (node.key) {
        const parent = this.getParentHierarchy(products, node.key);
        parent?.pop(); // remove the current node
        node.parent = parent ? parent.pop() : undefined;
      }
      resultValue.push({ key: node.key ?? '' });
    } else {
      // remove
      resultValue.splice(index, 1);
    }
    this.formRecipe?.markAsDirty();
    this.formRecipe?.patchValue({ productReferences: resultValue });
  }

  getParentHierarchy(array: ProductNode[], key: string): ProductNode[] | undefined {
    if (typeof array !== 'undefined') {
      for (const obj of array) {
        if (obj.key === key) {
          return [obj];
        }
        const a = this.getParentHierarchy(obj.children ?? [], key);
        if (a !== null && a !== undefined) {
          a.unshift(obj);
          return a;
        }
      }
    }
    return undefined;
  }

  isProductSelected(key: string): boolean {
    return this.formRecipe?.value.productReferences.some((keyProd: any) => keyProd.key === key);
  }
}
